/**
 * Accessibility layout presets — framework-agnostic settings shared by the
 * web app and (future) native shells.
 *
 * Stored in user `settingsJson.accessibility`. Presets are person-centred
 * starting points aligned with NDIS practice: participants choose what helps
 * rather than a one-size-fits-all UI.
 */

/** Named layout preset — each maps to a bundle of CSS variables + nav flags. */
export type AccessibilityPresetId =
  | 'default'
  /** Larger type, spacing, and 48px minimum touch targets (motor / vision). */
  | 'large-touch'
  /** High-contrast palette and stronger focus rings (low vision). */
  | 'high-contrast'
  /** Respects prefers-reduced-motion and disables decorative animation. */
  | 'reduced-motion'
  /**
   * Fewer primary nav destinations — Sessions, Direct, Calls only.
   * Reduces cognitive load for participants who find dense menus overwhelming.
   */
  | 'simplified-nav'
  /**
   * Clearer hierarchy: wider line-height, fewer secondary actions per screen,
   * voice-first hint labels on key CTAs.
   */
  | 'cognitive-clarity'

export interface AccessibilitySettings {
  preset: AccessibilityPresetId
  /** Optional fine-grained overrides (applied on top of the preset). */
  reducedMotion?: boolean
  highContrast?: boolean
  largeText?: boolean
  simplifiedNav?: boolean
  voiceHints?: boolean
}

export const DEFAULT_ACCESSIBILITY_SETTINGS: AccessibilitySettings = {
  preset: 'default',
}

/** CSS custom properties applied on `html[data-a11y-preset]`. */
export interface AccessibilityCssVars {
  '--a11y-font-scale': string
  '--a11y-touch-min': string
  '--a11y-line-height': string
  '--a11y-content-max-width': string
  '--a11y-focus-ring-width': string
  '--a11y-nav-density': string
}
